import { makeVar } from '@apollo/client'
import { useSubscription } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { useCallback } from 'react'

import { Permission, useCheckPermission } from '@/features/auth'

const recentlyRead = makeVar(new Set<number>())

/**
 * Hook to track orders that have been recently marked as read by the current user.
 *
 * **Problem this solves:**
 * When an admin marks an order as read, we have a race condition between:
 * 1. The mutation response (updates cache immediately)
 * 2. The subscription event (broadcasts the same change to all clients)
 *
 * Without tracking, both events would try to decrement the unread counter,
 * causing the count to be incorrect (-2 instead of -1).
 *
 * **How it works:**
 * - Call this function with an order ID right before executing a "mark as read" mutation
 * - The order ID gets stored in a reactive Apollo variable (`recentlyRead`)
 * - The subscription handler checks this set and skips counter updates for tracked orders
 * - After 5 seconds, the order ID is automatically removed from tracking
 *
 * **Usage:**
 * ```typescript
 * const addRecentlyRead = useAddRecentlyRead()
 *
 * const markAsRead = () => {
 *   addRecentlyRead(order.id)  // Track this action
 *   markReadMutation({ variables: { id: order.id } })
 * }
 * ```
 *
 * **Timing:**
 * The 5-second cleanup ensures we don't permanently ignore updates for an order,
 * while being long enough to handle slow network conditions and out-of-order events.
 *
 * @param orderId - The ID of the order being marked as read
 * @returns A memoized function to add an order to the recently read tracking set
 */
export function useAddRecentlyRead() {
  return useCallback((orderId: number) => {
    const set = new Set(recentlyRead())
    set.add(orderId)
    recentlyRead(set)

    // Make sure we clean it up afterwards
    setTimeout(() => {
      const set = new Set(recentlyRead())
      set.delete(orderId)
      recentlyRead(set)
    }, 5000)
  }, [])
}

export function useOrdersSubscriptions() {
  const canSeeOrders = useCheckPermission([Permission.ORDER_READ])

  useSubscription(AdminAPI.order.UPDATED, {
    skip: !canSeeOrders,
    onData({ data, client }) {
      if (!data.data) return

      const cache = client.cache

      const order = getFragment(
        SharedAPI.order.ORDER_FRAGMENT,
        data.data.orderUpdated
      )

      let unreadCountIncrease = 0
      const recentReads = new Set(recentlyRead())

      cache.updateQuery(
        {
          query: AdminAPI.order.LIST_REQUESTS
        },
        data => {
          if (!data) return

          let didEditOrder = false

          // If the user marked it as read himself, we count down as well
          if (recentReads.has(order.id) && order.isRead) {
            unreadCountIncrease = -1
            recentReads.delete(order.id)

            return
          }

          const itemsFrag = getFragment(
            SharedAPI.order.ORDER_FRAGMENT,
            data.listRequestOrders.items
          )

          const items = itemsFrag
            .map(function (item) {
              if (item.id === order.id) {
                didEditOrder = true

                // When the order changed to isRead we should count down
                if (order.isRead && !item.isRead) unreadCountIncrease = -1
                // However, when the order changed and is now unread, we should count up
                if (!order.isRead && item.isRead) unreadCountIncrease = 1

                return order
              }

              // Lastly if the order is completely new and unread we should count up
              unreadCountIncrease = order.isRead ? 0 : 1

              return item
            })
            .toSorted((oA, oB) => {
              const isReadA = oA.isRead ? 1 : 0
              const isReadB = oB.isRead ? 1 : 0

              if (isReadA - isReadB) return isReadA! - isReadB

              return (
                new Date(oB.updatedAt).getTime() -
                new Date(oA.updatedAt).getTime()
              )
            })

          return {
            listRequestOrders: {
              ...data?.listRequestOrders,
              items: didEditOrder ? items : [order, ...items]
            }
          }
        }
      )

      recentlyRead(recentReads)

      if (unreadCountIncrease !== 0) {
        cache.updateQuery(
          {
            query: AdminAPI.order.UNREAD_COUNT
          },
          data => {
            if (!data) return

            return {
              ordersUnreadCount: data.ordersUnreadCount + unreadCountIncrease
            }
          }
        )
      }

      // We need to reload participants
      if (order.type === GraphQL.OrderType.Event) {
        client.refetchQueries({ include: [AdminAPI.order.GET_BY_EVENT] })
      }
    }
  })
}
